/**
 * ForHireCard Component
 *
 * Card for displaying coaches/experts available for hire in the marketplace.
 *
 * SECURITY NOTE: Audio playback (Preview/Listen Full) is NOT implemented here.
 * The marketplace /profiles endpoint does NOT return audio URLs - this is intentional.
 * Audio must be accessed through the credit-gated endpoints:
 * - GET /api/public-profiles/:slug/clips/:clipId/preview (free, truncated)
 * - GET /api/public-profiles/:slug/clips/:clipId/listen (auth + credits required)
 *
 * To listen to audio, users should navigate to the public profile page where
 * the proper gated listen flow is implemented.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { useCreateHireRequestMutation } from '../../store/api/marketplaceApi';
import {
  useSaveCandidateMutation,
  useUnsaveCandidateMutation,
  useCheckCandidateSavedQuery,
} from '../../store/api/recruiterApi';
import { ShareProfileModal } from '../profile/ShareProfileModal';
import { useCentralConsumer } from '../../hooks/useCentralConsumer';

export type AuthorityLevelKey = 'novice' | 'bronze' | 'silver' | 'gold' | 'platinum';

/**
 * MarketplacePerson interface
 *
 * IMPORTANT: This interface must match the actual API response from
 * GET /marketplace/profiles. Do NOT add fields that don't exist in the API.
 *
 * Audio URLs are intentionally NOT included here - audio access must go
 * through the credit-gated listen endpoints on public profiles.
 */
export interface MarketplacePerson {
  userId: string;
  displayName: string;
  headline?: string;
  avatarUrl?: string;
  publicSlug?: string;
  portfolioId?: string;
  domains: string[];
  badges: string[];
  hourlyRate?: number | null;
  sessionRate?: number | null;
  currency?: string;
  sessionsCompleted?: number;
  averageRating?: number | null;
  isCoach: boolean;
  isAvailableForHire: boolean;
  location?: string;
  overallScore?: number | null;
  authorityLevel?: AuthorityLevelKey;
  // NOTE: pitchAudioUrl is intentionally NOT included - see SECURITY NOTE above
}

// Authority level badge colors
const AUTHORITY_COLORS: Record<AuthorityLevelKey, { bg: string; text: string }> = {
  novice: { bg: 'bg-gray-100', text: 'text-gray-700' },
  bronze: { bg: 'bg-amber-100', text: 'text-amber-700' },
  silver: { bg: 'bg-slate-200', text: 'text-slate-700' },
  gold: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  platinum: { bg: 'bg-purple-100', text: 'text-purple-700' },
};

export interface ForHireCardProps {
  person: MarketplacePerson;
  onHireClick?: (person: MarketplacePerson) => void;
}

export function ForHireCard({ person, onHireClick }: ForHireCardProps) {
  const navigate = useNavigate();
  const [createHireRequest, { isLoading: isRequesting }] = useCreateHireRequestMutation();
  const [saveCandidate] = useSaveCandidateMutation();
  const [unsaveCandidate] = useUnsaveCandidateMutation();
  // The shortlist is a recruiter tool (POST /recruiter/saved-candidates needs
  // recruiter:write). Anyone else got a heart that silently did nothing, and
  // every card fired a saved-check request that could only be refused.
  const { hasCapability } = useCentralConsumer();
  const canShortlist = hasCapability('recruiter', 'write');
  const { data: savedCheck } = useCheckCandidateSavedQuery(person.userId, { skip: !person.userId || !canShortlist });
  const [isSaved, setIsSaved] = useState(false);

  // Sync saved state from API
  if (savedCheck?.isSaved && !isSaved) setIsSaved(true);

  const handleToggleSave = async () => {
    try {
      if (isSaved) {
        await unsaveCandidate(person.userId).unwrap();
        setIsSaved(false);
      } else {
        await saveCandidate({ candidateUserId: person.userId }).unwrap();
        setIsSaved(true);
      }
    } catch {
      // Silently fail — recruiter feature may not be available for all users
    }
  };

  // Local state
  const [showHireModal, setShowHireModal] = useState(false);
  const [hireMessage, setHireMessage] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [hireRequestStatus, setHireRequestStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const initials = getInitials(person.displayName);
  const topDomains = person.domains.slice(0, 2);
  const currency = person.currency || 'USD';

  // Only a published profile has a page (/u/:slug). There is no profile page
  // by user id: the old fallback navigated to a route that does not exist and
  // bounced the viewer to the home page.
  const profileHref = person.publicSlug ? `/u/${person.publicSlug}` : null;
  const handleViewProfile = () => {
    if (profileHref) navigate(profileHref);
  };

  const handleHireClick = () => {
    if (onHireClick) {
      onHireClick(person);
    } else {
      setShowHireModal(true);
    }
  };

  const handleSubmitHireRequest = async () => {
    if (!person.portfolioId) {
      setHireRequestStatus('error');
      return;
    }

    try {
      await createHireRequest({
        targetUserId: person.userId,
        portfolioId: person.portfolioId,
        message: hireMessage || `I'd like to connect with you about a coaching session.`,
      }).unwrap();
      setShowHireModal(false);
      setHireMessage('');
      setHireRequestStatus('success');
    } catch {
      setHireRequestStatus('error');
    }
  };

  return (
    <>
      <div className="card hover:shadow-lg transition-shadow">
        <div className="card-body">
          {/* Header: avatar + name + score */}
          <div className="flex items-start gap-4">
            {person.avatarUrl ? (
              <img
                src={person.avatarUrl}
                alt={person.displayName}
                className="w-14 h-14 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                {initials}
              </div>
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900 truncate">
                  {person.displayName}
                </h3>
                {person.isAvailableForHire && (
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                    Available
                  </span>
                )}
              </div>
              {person.headline && (
                <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">{person.headline}</p>
              )}
              {person.location && (
                <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                  <LocationIcon className="w-3 h-3" />
                  {person.location}
                </p>
              )}
            </div>

            {/* Performance score — primary comparison signal */}
            {person.overallScore != null && (
              <div className="flex-shrink-0 text-center">
                <div
                  className={clsx(
                    'w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold',
                    person.overallScore >= 90
                      ? 'bg-green-100 text-green-700'
                      : person.overallScore >= 80
                      ? 'bg-primary-100 text-primary-700'
                      : person.overallScore >= 60
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-gray-100 text-gray-700'
                  )}
                >
                  {person.overallScore}
                </div>
              </div>
            )}
          </div>

          {/* Key signals — clean, scannable row */}
          <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t text-xs text-gray-500">
            {person.overallScore != null && (
              <span className="font-medium text-gray-700">
                {person.overallScore} Performance
              </span>
            )}
            {topDomains.length > 0 && (
              <span>
                {topDomains.map(formatDomain).join(', ')}
              </span>
            )}
            {person.authorityLevel && person.authorityLevel !== 'novice' && (
              <span className={`px-1.5 py-0.5 rounded ${AUTHORITY_COLORS[person.authorityLevel].bg} ${AUTHORITY_COLORS[person.authorityLevel].text}`}>
                {person.authorityLevel.charAt(0).toUpperCase() + person.authorityLevel.slice(1)}
              </span>
            )}
            {person.isCoach && (
              <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">Coach</span>
            )}
            {person.averageRating != null && (
              <span className="flex items-center gap-1">
                <StarIcon className="w-3.5 h-3.5 text-yellow-400" />
                {person.averageRating.toFixed(1)}
              </span>
            )}
          </div>

          {/* Rate */}
          {(person.hourlyRate || person.sessionRate) && (
            <div className="mt-2 text-sm font-medium text-gray-900">
              {person.hourlyRate ? (
                <span>From {formatCurrency(person.hourlyRate, currency)}/hr</span>
              ) : person.sessionRate ? (
                <span>From {formatCurrency(person.sessionRate, currency)}/session</span>
              ) : null}
            </div>
          )}

          {/* Actions — clear hierarchy */}
          <div className="flex gap-2 mt-4">
            {profileHref ? (
              <button
                onClick={handleViewProfile}
                className="flex-1 btn btn-primary text-sm"
              >
                View Profile
              </button>
            ) : (
              <span className="flex-1 text-xs text-gray-500 self-center text-center" data-testid="profile-not-public">
                Profile not published
              </span>
            )}
            {person.isAvailableForHire && (
              <button
                onClick={handleHireClick}
                disabled={isRequesting || hireRequestStatus === 'success'}
                className="flex-1 btn btn-outline text-sm"
              >
                {isRequesting ? 'Sending...' : hireRequestStatus === 'success' ? 'Contacted' : 'Contact'}
              </button>
            )}
            {canShortlist && (
              <button
                onClick={handleToggleSave}
                className={`btn btn-outline text-sm px-3 ${isSaved ? 'text-red-500 border-red-200 hover:bg-red-50' : ''}`}
                title={isSaved ? 'Remove from shortlist' : 'Save to shortlist'}
              >
                <HeartIcon className="w-4 h-4" filled={isSaved} />
              </button>
            )}
            {person.publicSlug && (
              <button
                onClick={() => setShowShareModal(true)}
                className="btn btn-outline text-sm px-3"
                title="Share"
              >
                <ShareIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          {hireRequestStatus === 'success' && (
            <p className="text-xs text-green-600 mt-1">Contact request sent</p>
          )}
          {hireRequestStatus === 'error' && (
            <p className="text-xs text-red-600 mt-1">Failed to send. Try again.</p>
          )}
        </div>
      </div>

      {/* Hire Request Modal */}
      {showHireModal && (
        <HireRequestModal
          person={person}
          message={hireMessage}
          onMessageChange={setHireMessage}
          onSubmit={handleSubmitHireRequest}
          onClose={() => setShowHireModal(false)}
          isSubmitting={isRequesting}
        />
      )}

      {/* Share Profile Modal */}
      {showShareModal && person.publicSlug && (
        <ShareProfileModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          publicSlug={person.publicSlug}
          displayName={person.displayName}
        />
      )}
    </>
  );
}

// Hire Request Modal
interface HireRequestModalProps {
  person: MarketplacePerson;
  message: string;
  onMessageChange: (value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
  isSubmitting: boolean;
}

function HireRequestModal({
  person,
  message,
  onMessageChange,
  onSubmit,
  onClose,
  isSubmitting,
}: HireRequestModalProps) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">
              Request Session with {person.displayName}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Send a message to introduce yourself and explain what you're looking for.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Message
              </label>
              <textarea
                value={message}
                onChange={(e) => onMessageChange(e.target.value)}
                placeholder="Hi! I'm interested in a coaching session to improve my..."
                className="input w-full h-32 resize-none"
              />
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={onClose}
                className="flex-1 btn btn-outline"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={onSubmit}
                className="flex-1 btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Request'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper functions
function getInitials(name: string): string {
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
}

function formatDomain(domain: string): string {
  return domain
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// Icon components
function LocationIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
      />
    </svg>
  );
}

function HeartIcon({ className, filled = false }: { className?: string; filled?: boolean }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
      />
    </svg>
  );
}
